import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaArrowRight } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import useNews from '../hooks/useNews';
import { formatDate } from '../utils/stringUtils';

const NewsList = () => {
  const { news, loading, error } = useNews();

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-purple-main flex items-center justify-center">
        <div className="neon-loader">
          <div className="circle"></div>
          <div className="circle"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-dark-purple-main flex flex-col items-center justify-center text-white">
        <h2 className="text-2xl font-bold mb-4">Lỗi khi tải tin tức</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Tin tức mới tại PSA Roleplay"
        description="Cập nhật tin tức mới nhất từ PSA Roleplay. Theo dõi thông tin về cập nhật, sự kiện và hướng dẫn mới nhất."
      />
      
      <Helmet>
        <script type="application/ld+json">
        {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Tin tức mới tại PSA Roleplay",
            "description": "Cập nhật tin tức mới nhất từ PSA Roleplay. Theo dõi thông tin về cập nhật, sự kiện và hướng dẫn mới nhất.",
            "url": "https://psa.world/news"
        })}
        </script>

        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Trang chủ",
              "item": "https://psa.world"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Tin tức tại PSA Roleplay",
              "item": "https://psa.world/news"
            }
          ]
        })}
        </script>
      </Helmet>

      <div className="min-h-screen pt-24 pb-16 bg-dark-purple-main relative overflow-hidden">
        <div className="page-news-bg absolute inset-0 opacity-30"></div>
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-dark-purple to-dark-purple-main"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div 
              className="text-center mb-12 mt-8"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-4">
                <span className="text-white neon-glow-white">TIN TỨC</span>
              </h1>

              <motion.div 
                className="w-24 h-1 bg-accent-teal mx-auto mb-6 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: 96 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              />
              
              <p className="text-light/80 text-lg max-w-2xl mx-auto">
                Tải xuống phiên bản launcher mới nhất của chúng tôi để bắt đầu trải nghiệm PSA Online tốt nhất. Chúng tôi hỗ trợ cả Windows và Android.
              </p>
            
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {news.map((item, index) => (
                <motion.div
                  key={item.id}
                  className="glassmorphism-card overflow-hidden rounded-xl relative"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                >
                  <Link to={`/news/${item.slug}`} className="block">
                    <div 
                      className="aspect-[16/9] bg-cover bg-center"
                      style={{ backgroundImage: `url(${item.image})` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/60 to-transparent hover:from-dark/90 hover:via-dark/50 transition-all duration-300"></div>
                      
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <div className="flex items-center mb-3">
                          <FaCalendarAlt className="text-accent-teal mr-2" />
                          <span className="text-light/80 text-sm">{formatDate(item.date)}</span>
                        </div>
                        
                        <h2 className="text-xl font-bold mb-3 text-white line-clamp-2">
                          {item.title}
                        </h2>
                        
                        <p className="text-light/80 mb-4 line-clamp-2">
                          {item.summary}
                        </p>
                        
                        <div className="inline-flex items-center text-accent-teal hover:text-accent-teal/80 transition-colors">
                          Xem chi tiết <FaArrowRight className="ml-2" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
            
            {news.length === 0 && (
              <div className="glassmorphism-card p-8 text-center">
                <p className="text-xl text-light/80">Hiện chưa có bài viết nào được đăng.</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default NewsList;
